import React from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';

const SlideContainer = styled.div`
  width: 100%;
  height: 100%;
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
  padding: 2rem;
  position: relative;
  overflow: hidden;

  @media (max-width: 768px) {
    padding: 1.5rem;
    min-height: 100vh;
  }

  @media (max-width: 480px) {
    padding: 1rem;
    min-height: 100vh;
  }
`;

const BackgroundPattern = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: 
    radial-gradient(circle at 20% 80%, rgba(111, 26, 63, 0.1) 0%, transparent 50%),
    radial-gradient(circle at 80% 20%, rgba(200, 43, 63, 0.1) 0%, transparent 50%),
    radial-gradient(circle at 40% 40%, rgba(111, 26, 63, 0.05) 0%, transparent 50%);
`;

const HeroContent = styled.div`
  text-align: center;
  max-width: 1200px;
  padding: 0 1rem;
  position: relative;
  z-index: 10;

  @media (max-width: 768px) {
    padding: 0 0.5rem;
  }

  @media (max-width: 480px) {
    padding: 0;
  }
`;

const CompanyName = styled(motion.h1)`
  font-size: 6rem;
  font-weight: 900;
  margin-bottom: 1rem;
  background: linear-gradient(45deg, #6F1A3F, #C82B3F);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  line-height: 1.1;
  text-align: center;
  word-break: break-word;

  @media (max-width: 1024px) {
    font-size: 5rem;
  }

  @media (max-width: 768px) {
    font-size: 3.5rem;
    margin-bottom: 0.8rem;
  }

  @media (max-width: 480px) {
    font-size: 2.5rem;
    margin-bottom: 0.6rem;
  }

  @media (max-width: 360px) {
    font-size: 2rem;
    margin-bottom: 0.5rem;
  }
`;

const DigitalLabs = styled(motion.div)`
  font-size: 2.5rem;
  font-weight: 600;
  color: #fff;
  margin-bottom: 2rem;
  letter-spacing: 0.1em;
  text-align: center;

  @media (max-width: 1024px) {
    font-size: 2rem;
  }

  @media (max-width: 768px) {
    font-size: 1.6rem;
    margin-bottom: 1.5rem;
  }

  @media (max-width: 480px) {
    font-size: 1.3rem;
    margin-bottom: 1rem;
  }

  @media (max-width: 360px) {
    font-size: 1.1rem;
    margin-bottom: 0.8rem;
  }
`;

const Tagline = styled(motion.p)`
  font-size: 2rem;
  color: #ccc;
  margin-bottom: 3rem;
  font-weight: 300;
  max-width: 800px;
  line-height: 1.4;
  text-align: center;

  @media (max-width: 1024px) {
    font-size: 1.8rem;
  }

  @media (max-width: 768px) {
    font-size: 1.3rem;
    margin-bottom: 2rem;
  }

  @media (max-width: 480px) {
    font-size: 1.1rem;
    margin-bottom: 1.5rem;
  }

  @media (max-width: 360px) {
    font-size: 1rem;
    margin-bottom: 1rem;
  }
`;

const Subtitle = styled(motion.div)`
  font-size: 1.2rem;
  color: #888;
  font-weight: 400;
  letter-spacing: 0.05em;
`;

const FloatingElements = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  pointer-events: none;
  z-index: 1;

  @media (max-width: 768px) {
    display: none;
  }
`;

const MobileFloatingElements = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  pointer-events: none;
  z-index: 1;
  display: none;

  @media (max-width: 768px) {
    display: block;
  }
`;

const FloatingElement = styled(motion.div)`
  position: absolute;
  width: 60px;
  height: 60px;
  background: rgba(111, 26, 63, 0.1);
  border: 2px solid rgba(200, 43, 63, 0.3);
  border-radius: 15px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.5rem;
  backdrop-filter: blur(10px);
  transition: all 0.3s ease;

  &:hover {
    background: rgba(111, 26, 63, 0.2);
    border-color: rgba(200, 43, 63, 0.6);
    transform: scale(1.1);
  }

  @media (max-width: 768px) {
    width: 40px;
    height: 40px;
    font-size: 1rem;
    border-radius: 10px;
  }

  @media (max-width: 480px) {
    width: 35px;
    height: 35px;
    font-size: 0.9rem;
    border-radius: 8px;
  }
`;

const Slide1 = () => {
  return (
    <SlideContainer>
      <BackgroundPattern />
      <FloatingElements>
        {/* Technology Icons - Left Side */}
        <FloatingElement
          className="mobile-hidden"
          style={{ 
            top: '20%', 
            left: '5%',
            '@media (max-width: 768px)': {
              top: '15%',
              left: '3%'
            }
          }}
          animate={{
            y: [0, -25, 0],
            rotate: [0, 5, 0],
            opacity: [0.7, 1, 0.7],
          }}
          transition={{
            duration: 4,
            repeat: Infinity,
            ease: "easeInOut"
          }}
        >
          💻
        </FloatingElement>
        
        <FloatingElement
          style={{ top: '35%', left: '8%' }}
          animate={{
            y: [0, 20, 0],
            rotate: [0, -5, 0],
            opacity: [0.6, 0.9, 0.6],
          }}
          transition={{
            duration: 3.5,
            repeat: Infinity,
            ease: "easeInOut",
            delay: 0.5
          }}
        >
          🤖
        </FloatingElement>

        <FloatingElement
          style={{ top: '50%', left: '5%' }}
          animate={{
            y: [0, -15, 0],
            rotate: [0, 3, 0],
            opacity: [0.5, 0.8, 0.5],
          }}
          transition={{
            duration: 5,
            repeat: Infinity,
            ease: "easeInOut",
            delay: 1
          }}
        >
          🔒
        </FloatingElement>

        <FloatingElement
          style={{ top: '65%', left: '8%' }}
          animate={{
            y: [0, 18, 0],
            rotate: [0, -3, 0],
            opacity: [0.6, 0.9, 0.6],
          }}
          transition={{
            duration: 3.7,
            repeat: Infinity,
            ease: "easeInOut",
            delay: 1.7
          }}
        >
          🔧
        </FloatingElement>

        {/* Marketing Icons - Right Side */}
        <FloatingElement
          style={{ top: '20%', right: '5%' }}
          animate={{
            y: [0, 25, 0],
            rotate: [0, -3, 0],
            opacity: [0.6, 1, 0.6],
          }}
          transition={{
            duration: 4.5,
            repeat: Infinity,
            ease: "easeInOut",
            delay: 1.5
          }}
        >
          📈
        </FloatingElement>

        <FloatingElement
          style={{ top: '35%', right: '8%' }}
          animate={{
            y: [0, -20, 0],
            rotate: [0, 4, 0],
            opacity: [0.7, 0.9, 0.7],
          }}
          transition={{
            duration: 3.8,
            repeat: Infinity,
            ease: "easeInOut",
            delay: 2.2
          }}
        >
          🎨
        </FloatingElement>

        <FloatingElement
          style={{ top: '50%', right: '5%' }}
          animate={{
            y: [0, 15, 0],
            rotate: [0, -2, 0],
            opacity: [0.5, 0.8, 0.5],
          }}
          transition={{
            duration: 4.2,
            repeat: Infinity,
            ease: "easeInOut",
            delay: 0.8
          }}
        >
          📱
        </FloatingElement>

        <FloatingElement
          style={{ top: '65%', right: '8%' }}
          animate={{
            y: [0, -18, 0],
            rotate: [0, 3, 0],
            opacity: [0.6, 0.9, 0.6],
          }}
          transition={{
            duration: 3.4,
            repeat: Infinity,
            ease: "easeInOut",
            delay: 0.9
          }}
        >
          🎯
        </FloatingElement>

        {/* Development Icons - Top */}
        <FloatingElement
          style={{ top: '10%', left: '25%' }}
          animate={{
            y: [0, 18, 0],
            rotate: [0, -4, 0],
            opacity: [0.6, 0.9, 0.6],
          }}
          transition={{
            duration: 3.2,
            repeat: Infinity,
            ease: "easeInOut",
            delay: 1.8
          }}
        >
          ⚙️
        </FloatingElement>

        <FloatingElement
          style={{ top: '10%', right: '25%' }}
          animate={{
            y: [0, -22, 0],
            rotate: [0, 3, 0],
            opacity: [0.7, 1, 0.7],
          }}
          transition={{
            duration: 4.8,
            repeat: Infinity,
            ease: "easeInOut",
            delay: 2.5
          }}
        >
          🌐
        </FloatingElement>

        <FloatingElement
          style={{ top: '10%', left: '50%' }}
          animate={{
            y: [0, -24, 0],
            rotate: [0, 2, 0],
            opacity: [0.6, 0.9, 0.6],
          }}
          transition={{
            duration: 3.8,
            repeat: Infinity,
            ease: "easeInOut",
            delay: 1.4
          }}
        >
          🛠️
        </FloatingElement>

        {/* Digital Icons - Bottom */}
        <FloatingElement
          style={{ top: '80%', left: '25%' }}
          animate={{
            y: [0, 20, 0],
            rotate: [0, -3, 0],
            opacity: [0.5, 0.8, 0.5],
          }}
          transition={{
            duration: 3.6,
            repeat: Infinity,
            ease: "easeInOut",
            delay: 0.3
          }}
        >
          🥽
        </FloatingElement>

        <FloatingElement
          style={{ top: '80%', right: '25%' }}
          animate={{
            y: [0, -18, 0],
            rotate: [0, 2, 0],
            opacity: [0.6, 0.9, 0.6],
          }}
          transition={{
            duration: 4.3,
            repeat: Infinity,
            ease: "easeInOut",
            delay: 1.2
          }}
        >
          💡
        </FloatingElement>

        <FloatingElement
          style={{ top: '80%', left: '50%' }}
          animate={{
            y: [0, 25, 0],
            rotate: [0, -2, 0],
            opacity: [0.7, 1, 0.7],
          }}
          transition={{
            duration: 3.9,
            repeat: Infinity,
            ease: "easeInOut",
            delay: 2.8
          }}
        >
          🚀
        </FloatingElement>

        {/* Programming Languages - Left Side Additional */}
        <FloatingElement
          style={{ top: '25%', left: '12%' }}
          animate={{
            y: [0, -18, 0],
            rotate: [0, 4, 0],
            opacity: [0.6, 0.9, 0.6],
          }}
          transition={{
            duration: 3.3,
            repeat: Infinity,
            ease: "easeInOut",
            delay: 1.1
          }}
        >
          ⚛️
        </FloatingElement>

        <FloatingElement
          style={{ top: '40%', left: '12%' }}
          animate={{
            y: [0, 22, 0],
            rotate: [0, -3, 0],
            opacity: [0.7, 1, 0.7],
          }}
          transition={{
            duration: 4.1,
            repeat: Infinity,
            ease: "easeInOut",
            delay: 2.3
          }}
        >
          🟨
        </FloatingElement>

        <FloatingElement
          style={{ top: '55%', left: '12%' }}
          animate={{
            y: [0, -16, 0],
            rotate: [0, 2, 0],
            opacity: [0.5, 0.8, 0.5],
          }}
          transition={{
            duration: 3.6,
            repeat: Infinity,
            ease: "easeInOut",
            delay: 0.8
          }}
        >
          🐍
        </FloatingElement>

        <FloatingElement
          style={{ top: '70%', left: '12%' }}
          animate={{
            y: [0, 19, 0],
            rotate: [0, -4, 0],
            opacity: [0.6, 0.9, 0.6],
          }}
          transition={{
            duration: 4.4,
            repeat: Infinity,
            ease: "easeInOut",
            delay: 1.9
          }}
        >
          ☕
        </FloatingElement>

        {/* Programming Languages - Right Side Additional */}
        <FloatingElement
          style={{ top: '25%', right: '12%' }}
          animate={{
            y: [0, 20, 0],
            rotate: [0, -2, 0],
            opacity: [0.6, 0.9, 0.6],
          }}
          transition={{
            duration: 3.7,
            repeat: Infinity,
            ease: "easeInOut",
            delay: 1.4
          }}
        >
          🔷
        </FloatingElement>

        <FloatingElement
          style={{ top: '40%', right: '12%' }}
          animate={{
            y: [0, -21, 0],
            rotate: [0, 3, 0],
            opacity: [0.7, 1, 0.7],
          }}
          transition={{
            duration: 4.2,
            repeat: Infinity,
            ease: "easeInOut",
            delay: 2.6
          }}
        >
          🟦
        </FloatingElement>

        <FloatingElement
          style={{ top: '55%', right: '12%' }}
          animate={{
            y: [0, 17, 0],
            rotate: [0, -3, 0],
            opacity: [0.5, 0.8, 0.5],
          }}
          transition={{
            duration: 3.8,
            repeat: Infinity,
            ease: "easeInOut",
            delay: 0.6
          }}
        >
          🟢
        </FloatingElement>

        <FloatingElement
          style={{ top: '70%', right: '12%' }}
          animate={{
            y: [0, -20, 0],
            rotate: [0, 4, 0],
            opacity: [0.6, 0.9, 0.6],
          }}
          transition={{
            duration: 4.6,
            repeat: Infinity,
            ease: "easeInOut",
            delay: 2.1
          }}
        >
          🟣
        </FloatingElement>

        {/* Additional Top Row Icons */}
        <FloatingElement
          style={{ top: '8%', left: '35%' }}
          animate={{
            y: [0, 16, 0],
            rotate: [0, -2, 0],
            opacity: [0.6, 0.9, 0.6],
          }}
          transition={{
            duration: 3.5,
            repeat: Infinity,
            ease: "easeInOut",
            delay: 1.7
          }}
        >
          📊
        </FloatingElement>

        <FloatingElement
          style={{ top: '8%', right: '35%' }}
          animate={{
            y: [0, -19, 0],
            rotate: [0, 3, 0],
            opacity: [0.7, 1, 0.7],
          }}
          transition={{
            duration: 4.3,
            repeat: Infinity,
            ease: "easeInOut",
            delay: 2.4
          }}
        >
          ⚡
        </FloatingElement>

        {/* Additional Bottom Row Icons */}
        <FloatingElement
          style={{ top: '82%', left: '35%' }}
          animate={{
            y: [0, 18, 0],
            rotate: [0, -3, 0],
            opacity: [0.5, 0.8, 0.5],
          }}
          transition={{
            duration: 3.9,
            repeat: Infinity,
            ease: "easeInOut",
            delay: 1.3
          }}
        >
          🎮
        </FloatingElement>

        <FloatingElement
          style={{ top: '82%', right: '35%' }}
          animate={{
            y: [0, -17, 0],
            rotate: [0, 2, 0],
            opacity: [0.6, 0.9, 0.6],
          }}
          transition={{
            duration: 4.5,
            repeat: Infinity,
            ease: "easeInOut",
            delay: 2.7
          }}
        >
          🌟
        </FloatingElement>

        {/* Corner Icons */}
        <FloatingElement
          style={{ top: '15%', left: '15%' }}
          animate={{
            y: [0, -14, 0],
            rotate: [0, 2, 0],
            opacity: [0.5, 0.8, 0.5],
          }}
          transition={{
            duration: 3.2,
            repeat: Infinity,
            ease: "easeInOut",
            delay: 0.4
          }}
        >
          🔗
        </FloatingElement>

        <FloatingElement
          style={{ top: '15%', right: '15%' }}
          animate={{
            y: [0, 15, 0],
            rotate: [0, -2, 0],
            opacity: [0.6, 0.9, 0.6],
          }}
          transition={{
            duration: 3.8,
            repeat: Infinity,
            ease: "easeInOut",
            delay: 1.6
          }}
        >
          📢
        </FloatingElement>

        <FloatingElement
          style={{ top: '75%', left: '15%' }}
          animate={{
            y: [0, 13, 0],
            rotate: [0, 3, 0],
            opacity: [0.7, 1, 0.7],
          }}
          transition={{
            duration: 4.1,
            repeat: Infinity,
            ease: "easeInOut",
            delay: 2.9
          }}
        >
          👥
        </FloatingElement>

        <FloatingElement
          style={{ top: '75%', right: '15%' }}
          animate={{
            y: [0, -16, 0],
            rotate: [0, -3, 0],
            opacity: [0.5, 0.8, 0.5],
          }}
          transition={{
            duration: 3.4,
            repeat: Infinity,
            ease: "easeInOut",
            delay: 0.9
          }}
        >
          💎
        </FloatingElement>

      </FloatingElements>

      <MobileFloatingElements>
        {/* Mobile - Essential Icons Only */}
        {/* Left Side */}
        <FloatingElement
          style={{ top: '20%', left: '5%' }}
          animate={{
            y: [0, -20, 0],
            rotate: [0, 3, 0],
            opacity: [0.7, 1, 0.7],
          }}
          transition={{
            duration: 4,
            repeat: Infinity,
            ease: "easeInOut"
          }}
        >
          💻
        </FloatingElement>

        <FloatingElement
          style={{ top: '40%', left: '8%' }}
          animate={{
            y: [0, 15, 0],
            rotate: [0, -2, 0],
            opacity: [0.6, 0.9, 0.6],
          }}
          transition={{
            duration: 3.5,
            repeat: Infinity,
            ease: "easeInOut",
            delay: 1
          }}
        >
          🤖
        </FloatingElement>

        <FloatingElement
          style={{ top: '60%', left: '5%' }}
          animate={{
            y: [0, -12, 0],
            rotate: [0, 2, 0],
            opacity: [0.5, 0.8, 0.5],
          }}
          transition={{
            duration: 4.5,
            repeat: Infinity,
            ease: "easeInOut",
            delay: 2
          }}
        >
          🔒
        </FloatingElement>

        {/* Right Side */}
        <FloatingElement
          style={{ top: '20%', right: '5%' }}
          animate={{
            y: [0, 18, 0],
            rotate: [0, -3, 0],
            opacity: [0.6, 1, 0.6],
          }}
          transition={{
            duration: 3.8,
            repeat: Infinity,
            ease: "easeInOut",
            delay: 0.5
          }}
        >
          📈
        </FloatingElement>

        <FloatingElement
          style={{ top: '40%', right: '8%' }}
          animate={{
            y: [0, -15, 0],
            rotate: [0, 2, 0],
            opacity: [0.7, 0.9, 0.7],
          }}
          transition={{
            duration: 4.2,
            repeat: Infinity,
            ease: "easeInOut",
            delay: 1.5
          }}
        >
          🎨
        </FloatingElement>

        <FloatingElement
          style={{ top: '60%', right: '5%' }}
          animate={{
            y: [0, 12, 0],
            rotate: [0, -2, 0],
            opacity: [0.5, 0.8, 0.5],
          }}
          transition={{
            duration: 3.6,
            repeat: Infinity,
            ease: "easeInOut",
            delay: 2.5
          }}
        >
          📱
        </FloatingElement>

        {/* Top Row */}
        <FloatingElement
          style={{ top: '10%', left: '30%' }}
          animate={{
            y: [0, 15, 0],
            rotate: [0, -2, 0],
            opacity: [0.6, 0.9, 0.6],
          }}
          transition={{
            duration: 3.2,
            repeat: Infinity,
            ease: "easeInOut",
            delay: 0.8
          }}
        >
          ⚙️
        </FloatingElement>

        <FloatingElement
          style={{ top: '10%', right: '30%' }}
          animate={{
            y: [0, -18, 0],
            rotate: [0, 3, 0],
            opacity: [0.7, 1, 0.7],
          }}
          transition={{
            duration: 4.8,
            repeat: Infinity,
            ease: "easeInOut",
            delay: 1.8
          }}
        >
          🌐
        </FloatingElement>

        {/* Bottom Row */}
        <FloatingElement
          style={{ top: '80%', left: '30%' }}
          animate={{
            y: [0, 16, 0],
            rotate: [0, -2, 0],
            opacity: [0.5, 0.8, 0.5],
          }}
          transition={{
            duration: 3.6,
            repeat: Infinity,
            ease: "easeInOut",
            delay: 0.3
          }}
        >
          🥽
        </FloatingElement>

        <FloatingElement
          style={{ top: '80%', right: '30%' }}
          animate={{
            y: [0, -14, 0],
            rotate: [0, 2, 0],
            opacity: [0.6, 0.9, 0.6],
          }}
          transition={{
            duration: 4.3,
            repeat: Infinity,
            ease: "easeInOut",
            delay: 1.2
          }}
        >
          💡
        </FloatingElement>

        <FloatingElement
          style={{ top: '80%', left: '50%' }}
          animate={{
            y: [0, 20, 0],
            rotate: [0, -2, 0],
            opacity: [0.7, 1, 0.7],
          }}
          transition={{
            duration: 3.9,
            repeat: Infinity,
            ease: "easeInOut",
            delay: 2.8
          }}
        >
          🚀
        </FloatingElement>

        {/* Programming Languages - Mobile */}
        <FloatingElement
          style={{ top: '30%', left: '15%' }}
          animate={{
            y: [0, -12, 0],
            rotate: [0, 2, 0],
            opacity: [0.6, 0.9, 0.6],
          }}
          transition={{
            duration: 3.3,
            repeat: Infinity,
            ease: "easeInOut",
            delay: 1.1
          }}
        >
          ⚛️
        </FloatingElement>

        <FloatingElement
          style={{ top: '50%', left: '15%' }}
          animate={{
            y: [0, 14, 0],
            rotate: [0, -2, 0],
            opacity: [0.7, 1, 0.7],
          }}
          transition={{
            duration: 4.1,
            repeat: Infinity,
            ease: "easeInOut",
            delay: 2.3
          }}
        >
          🟨
        </FloatingElement>

        <FloatingElement
          style={{ top: '30%', right: '15%' }}
          animate={{
            y: [0, 16, 0],
            rotate: [0, -2, 0],
            opacity: [0.6, 0.9, 0.6],
          }}
          transition={{
            duration: 3.7,
            repeat: Infinity,
            ease: "easeInOut",
            delay: 1.4
          }}
        >
          🔷
        </FloatingElement>

        <FloatingElement
          style={{ top: '50%', right: '15%' }}
          animate={{
            y: [0, -13, 0],
            rotate: [0, 2, 0],
            opacity: [0.7, 1, 0.7],
          }}
          transition={{
            duration: 4.2,
            repeat: Infinity,
            ease: "easeInOut",
            delay: 2.6
          }}
        >
          🟦
        </FloatingElement>
      </MobileFloatingElements>

      <HeroContent>
        <CompanyName
          initial={{ opacity: 0, y: 50 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1, delay: 0.2 }}
        >
          3AM
        </CompanyName>
        
        <DigitalLabs
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1, delay: 0.4 }}
        >
          DIGITAL LABS
        </DigitalLabs>

        <Tagline
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1, delay: 0.6 }}
        >
          Pioneering Tomorrow's Solutions, Today
        </Tagline>

        <Subtitle
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1, delay: 0.8 }}
        >
          Comprehensive Digital Transformation & Strategic Marketing Excellence
        </Subtitle>
      </HeroContent>
    </SlideContainer>
  );
};

export default Slide1;
